import { useState, useEffect, useCallback, useMemo } from "react";
import { ResourceType } from "@/models/resource-types";

/**
 * Generic hook to manage groupBy preference for any resource type.
 * Uses localStorage for persistence and synchronization across tabs/components.
 *
 * This hook replaces the individual use-*-group-by.ts hooks with a single
 * parameterized implementation.
 *
 * @param resourceType - The resource type (tracker, enrichment, alert, etc.)
 * @param validValues - Array of valid groupBy values
 * @param defaultValue - Default groupBy value
 */
export function useResourceGroupBy<T extends string>(
  resourceType: ResourceType,
  validValues: readonly T[],
  defaultValue: T,
): { groupBy: T; setGroupBy: (value: T) => void } {
  const storageKey = useMemo(() => `${resourceType}GroupBy`, [resourceType]);
  const eventName = useMemo(
    () => `${resourceType}GroupByChange`,
    [resourceType],
  );

  const [groupBy, setGroupByState] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    const saved = localStorage.getItem(storageKey);
    if (saved && validValues.includes(saved as T)) {
      return saved as T;
    }
    return defaultValue;
  });

  // Listen for storage changes (for cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        if (validValues.includes(e.newValue as T)) {
          setGroupByState(e.newValue as T);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [storageKey, validValues]);

  const setGroupBy = useCallback(
    (value: T) => {
      setGroupByState(value);
      localStorage.setItem(storageKey, value);
      // Dispatch a custom event for same-tab synchronization
      window.dispatchEvent(new CustomEvent(eventName, { detail: value }));
    },
    [storageKey, eventName],
  );

  // Listen for same-tab changes
  useEffect(() => {
    const handleCustomChange = (e: CustomEvent<T>) => {
      setGroupByState(e.detail);
    };

    window.addEventListener(eventName, handleCustomChange as EventListener);
    return () =>
      window.removeEventListener(
        eventName,
        handleCustomChange as EventListener,
      );
  }, [eventName]);

  return { groupBy, setGroupBy };
}

// =============================================================================
// Type-safe wrappers for each resource type (for backward compatibility)
// =============================================================================

// Tracker
export type TrackerGroupBy = "none" | "source" | "tags" | "status" | "type";
const TRACKER_GROUP_BY_VALUES = [
  "none",
  "source",
  "tags",
  "status",
  "type",
] as const;
export const useTrackerGroupBy = () =>
  useResourceGroupBy<TrackerGroupBy>(
    "tracker",
    TRACKER_GROUP_BY_VALUES,
    "source",
  );

// Enrichment
export type EnrichmentGroupBy = "none" | "type" | "status";
const ENRICHMENT_GROUP_BY_VALUES = ["none", "type", "status"] as const;
export const useEnrichmentGroupBy = () =>
  useResourceGroupBy<EnrichmentGroupBy>(
    "enrichment",
    ENRICHMENT_GROUP_BY_VALUES,
    "status",
  );

// Alert
export type AlertGroupBy = "none" | "type" | "status";
const ALERT_GROUP_BY_VALUES = ["none", "type", "status"] as const;
export const useAlertGroupBy = () =>
  useResourceGroupBy<AlertGroupBy>("alert", ALERT_GROUP_BY_VALUES, "status");

// Widget
export type WidgetGroupBy = "none" | "type" | "dashboard";
const WIDGET_GROUP_BY_VALUES = ["none", "type", "dashboard"] as const;
export const useWidgetGroupBy = () =>
  useResourceGroupBy<WidgetGroupBy>("widget", WIDGET_GROUP_BY_VALUES, "none");

// Investigation
export type InvestigationGroupBy = "none" | "status";
const INVESTIGATION_GROUP_BY_VALUES = ["none", "status"] as const;
export const useInvestigationGroupBy = () =>
  useResourceGroupBy<InvestigationGroupBy>(
    "investigation",
    INVESTIGATION_GROUP_BY_VALUES,
    "none",
  );

// Dashboard
export type DashboardGroupBy = "none" | "recent" | "widgets_count";
const DASHBOARD_GROUP_BY_VALUES = ["none", "recent", "widgets_count"] as const;
export const useDashboardGroupBy = () =>
  useResourceGroupBy<DashboardGroupBy>(
    "dashboard",
    DASHBOARD_GROUP_BY_VALUES,
    "none",
  );

// Report
export type ReportGroupBy = "none" | "type" | "status";
const REPORT_GROUP_BY_VALUES = ["none", "type", "status"] as const;
export const useReportGroupBy = () =>
  useResourceGroupBy<ReportGroupBy>("report", REPORT_GROUP_BY_VALUES, "none");

// Conversation
export type ConversationGroupBy = "none" | "status";
const CONVERSATION_GROUP_BY_VALUES = ["none", "status"] as const;
export const useConversationGroupBy = () =>
  useResourceGroupBy<ConversationGroupBy>(
    "conversation",
    CONVERSATION_GROUP_BY_VALUES,
    "none",
  );

export default useResourceGroupBy;
